/**
 * Utility functions for handling currency formatting between German and English locales
 * Backend always expects and returns numbers in "123456.78" format
 * Frontend displays numbers based on user's locale (German: "123.456,78", English: "123,456.78")
 */

export interface LocaleConfig {
  decimalSeparator: string;
  thousandSeparator: string;
}

/**
 * Get locale configuration based on language
 */
export const getLocaleConfig = (language: string): LocaleConfig => {
  const isGerman = language.startsWith('de');
  return {
    decimalSeparator: isGerman ? ',' : '.',
    thousandSeparator: isGerman ? '.' : ','
  };
};

/**
 * Parse a locale-formatted string to a backend number format (123456.78)
 * Handles both German (1.234,56) and English (1,234.56) formats
 */
export const parseLocaleNumber = (value: string | number, language: string): string => {
  if (value === null || value === undefined || value === '') return '0.00';
  
  // If already a number, just format it
  if (typeof value === 'number') {
    return isNaN(value) ? '0.00' : value.toFixed(2);
  }
  
  const config = getLocaleConfig(language);
  let cleanValue = value.toString().trim();
  
  // Handle negative numbers
  const isNegative = cleanValue.startsWith('-');
  if (isNegative) cleanValue = cleanValue.substring(1);
  
  // Remove thousand separators
  cleanValue = cleanValue.split(config.thousandSeparator).join('');
  
  // Replace decimal separator with dot
  cleanValue = cleanValue.replace(config.decimalSeparator, '.');
  
  // Remove any remaining non-numeric characters except dot
  cleanValue = cleanValue.replace(/[^\d.]/g, '');
  
  // Parse and validate
  const num = parseFloat(cleanValue);
  if (isNaN(num)) return '0.00';
  
  // Format to 2 decimal places and add negative sign if needed
  return (isNegative ? '-' : '') + num.toFixed(2);
};

/**
 * Format a backend number (123456.78) to locale display format
 * German: 123.456,78
 * English: 123,456.78
 */
export const formatNumberForLocale = (
  value: string | number,
  language: string,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    showThousandSeparator?: boolean;
  }
): string => {
  const opts = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    showThousandSeparator: true,
    ...options
  };
  
  if (value === null || value === undefined || value === '') return '';
  
  // Convert to number
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  
  const config = getLocaleConfig(language);
  
  // Handle negative numbers
  const isNegative = num < 0;
  const absNum = Math.abs(num);
  
  // Format with fixed decimals
  const formatted = absNum.toFixed(opts.maximumFractionDigits);
  const [integerPart, decimalPart] = formatted.split('.');
  
  // Add thousand separators if requested
  let formattedInteger = integerPart;
  if (opts.showThousandSeparator && integerPart.length > 3) {
    formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, config.thousandSeparator);
  }
  
  // Combine parts with locale-specific decimal separator
  let result = formattedInteger;
  if (decimalPart !== undefined && opts.maximumFractionDigits > 0) {
    result += config.decimalSeparator + decimalPart;
  }
  
  // Add negative sign if needed
  return (isNegative ? '-' : '') + result;
};

/**
 * Smart number parser that auto-detects format (German or English)
 * Used when we don't know the source format
 */
export const smartParseNumber = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0;
  
  // If it's already a number, return it
  if (typeof value === 'number') return isNaN(value) ? 0 : value;
  
  // Convert to string for processing
  const stringValue = String(value).trim();
  
  // Check for both dots and commas
  const hasDot = stringValue.includes('.');
  const hasComma = stringValue.includes(',');
  
  if (hasDot && hasComma) {
    // Both separators present - need to determine format
    const lastDotIndex = stringValue.lastIndexOf('.');
    const lastCommaIndex = stringValue.lastIndexOf(',');
    
    if (lastCommaIndex > lastDotIndex) {
      // German format: dot for thousands, comma for decimal (1.234,56)
      const normalized = stringValue.replace(/\./g, '').replace(',', '.');
      const parsed = parseFloat(normalized);
      return isNaN(parsed) ? 0 : parsed;
    } else {
      // English format: comma for thousands, dot for decimal (1,234.56)
      const normalized = stringValue.replace(/,/g, '');
      const parsed = parseFloat(normalized);
      return isNaN(parsed) ? 0 : parsed;
    }
  } else if (hasComma && !hasDot) {
    // Only comma present
    const parts = stringValue.split(',');
    
    if (parts.length === 2) {
      const afterComma = parts[1];
      
      // If there are 1-2 digits after comma, likely German decimal
      if (afterComma.length <= 2) {
        const normalized = stringValue.replace(',', '.');
        const parsed = parseFloat(normalized);
        return isNaN(parsed) ? 0 : parsed;
      }
      // If exactly 3 digits after comma, likely English thousand separator
      else if (afterComma.length === 3) {
        const normalized = stringValue.replace(/,/g, '');
        const parsed = parseFloat(normalized);
        return isNaN(parsed) ? 0 : parsed;
      }
    }
    // Multiple commas - treat as thousand separators
    const normalized = stringValue.replace(/,/g, '');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : parsed;
  }
  
  // No special handling needed - parse directly
  const parsed = parseFloat(stringValue);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Validate if a string is a valid currency amount
 */
export const isValidCurrencyAmount = (value: string, language: string): boolean => {
  if (!value || value.trim() === '') return false;
  
  const trimmedValue = value.trim();
  const config = getLocaleConfig(language);
  
  // Build regex pattern based on locale
  if (config.decimalSeparator === ',') {
    // German format: optional minus, digits with optional dots as thousand separators, optional comma and up to 2 decimal digits
    // Valid: 123,45  1.234,56  -1.234,56  123  1.234
    // Invalid: 123.456 (3 digits after dot without comma)  1,234.56 (wrong separators)
    const germanPattern = /^-?\d{1,3}(?:\.\d{3})*(?:,\d{1,2})?$|^-?\d+(?:,\d{1,2})?$/;
    
    // Additional check: if it has dots, they should be thousand separators (groups of 3)
    if (trimmedValue.includes('.')) {
      const withoutSign = trimmedValue.replace(/^-/, '');
      const beforeDecimal = withoutSign.split(',')[0];
      const parts = beforeDecimal.split('.');
      
      // First part can be 1-3 digits, rest must be exactly 3
      if (parts.length > 1) {
        for (let i = 1; i < parts.length; i++) {
          if (parts[i].length !== 3) return false;
        }
      }
    }
    
    return germanPattern.test(trimmedValue);
  } else {
    // English format: optional minus, digits with optional commas as thousand separators, optional dot and up to 2 decimal digits
    // Valid: 123.45  1,234.56  -1,234.56  123  1,234
    // Invalid: 123,456 (3 digits after comma without dot)  1.234,56 (wrong separators)
    const englishPattern = /^-?\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?$|^-?\d+(?:\.\d{1,2})?$/;
    
    // Additional check: if it has commas, they should be thousand separators (groups of 3)
    if (trimmedValue.includes(',')) {
      const withoutSign = trimmedValue.replace(/^-/, '');
      const beforeDecimal = withoutSign.split('.')[0];
      const parts = beforeDecimal.split(',');
      
      // First part can be 1-3 digits, rest must be exactly 3
      if (parts.length > 1) {
        for (let i = 1; i < parts.length; i++) {
          if (parts[i].length !== 3) return false;
        }
      }
    }
    
    return englishPattern.test(trimmedValue);
  }
};

/**
 * Round a number to specified decimal places
 */
export const roundToDecimals = (value: number, decimals: number = 2): number => {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
};

/**
 * Calculate VAT amount from net amount and rate
 */
export const calculateVAT = (netAmount: number, vatRate: number): number => {
  return roundToDecimals((netAmount * vatRate) / 100);
};

/**
 * Calculate gross amount from net amount and VAT rate
 */
export const calculateGrossAmount = (netAmount: number, vatRate: number): number => {
  return roundToDecimals(netAmount + calculateVAT(netAmount, vatRate));
};